import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import Form from "../../../../components/forms/Form";
import { contactFields } from "../../../../constants/forms/contactFields";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import SectionHeader from "../../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../../hooks/useUrlFilters";

const CreateSupplierContactPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters: contactFilters, pagination: contactPagination } =
    useUrlFilters(
      {
        deleted_at: false,
      },
      `almox_supplierContacts`
    );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(contactFields)
  );

  // --- Mutação para criar um novo Contato ---
  const createContactMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.suppliers.supplierContactCreate(id),
        dataToCreate
      );
      return response;
    },
    onSuccess: (data) => {
      resetForm();
      queryClient.invalidateQueries({
        queryKey: [
          "almox_supplierContacts",
          id,
          contactFilters,
          contactPagination,
        ],
      });
      navigate(`/almoxarifado/fornecedores/detalhes/${id}`, {
        state: { type: "success" },
      });
    },
    onError: (err) => {
      console.log("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      await createContactMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Erro ao enviar contato:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createContactMutation]);

  const handleBack = useCallback(() => {
    navigate(`/almoxarifado/fornecedores/detalhes/${id}/`);
  }, [navigate, id]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return contactFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Contato do Fornecedor"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores/detalhes/${id}/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createContactMutation.isPending}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateSupplierContactPage;
